/**
 * Installation appointments API.
 *
 * A scheduled on-site robot installation. Lifecycle: Scheduled -> Completed | Void.
 * Carries the installation & deployment checklist (checklist_state, keyed by the item
 * ids in src/data/installationChecklist.ts), a table of deployed products, and a notes
 * section. Appointments created from an order (order_number set) complete that order
 * when the appointment is completed.
 */

import { API_BASE } from '../config/apiBase';
import { AttachmentVoidFields, patchAttachmentVoid } from './attachmentVoid';
import { actorJsonHeaders, withActor } from './session';

export type InstallationAppointmentStatus = 'Scheduled' | 'Completed' | 'Void';

export type ChecklistState = Record<string, string | boolean>;

export interface InstallationAssignee {
  user_id: number;
  username: string;
}

export interface InstallationProductRow {
  id: number;
  appointment_id: number;
  product_id: string | null;
  product_name: string;
  tracks_serial: boolean;
  unit_id: string | null;
  serial_number: string | null;
  quantity: number;
  needs_serial: boolean;
  created_at: string | null;
}

export interface InstallationAppointmentRow {
  id: number;
  client_id: number | null;
  /** The linked client's Job Code / Deployment ID, e.g. A-000101. */
  job_code: string | null;
  order_number: string | null;
  business_name: string;
  business_address: string | null;
  point_of_contact: string | null;
  poc_email: string | null;
  poc_phone: string | null;
  installation_at: string | null;
  /** Null for a single-day installation; set when the work spans a date range. */
  installation_end_at: string | null;
  status: InstallationAppointmentStatus;
  void_reason: string | null;
  checklist_state: ChecklistState;
  task_id: number | null;
  public_token: string | null;
  conflict_reason: string | null;
  conflict_author_name: string | null;
  conflict_reported_at: string | null;
  conflict_resolved_at: string | null;
  assignees: InstallationAssignee[];
  products: InstallationProductRow[];
  created_by_user_id: number | null;
  created_by_username: string | null;
  completed_at: string | null;
  voided_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface InstallationAppointmentsListResponse {
  scheduled: InstallationAppointmentRow[];
  completed: InstallationAppointmentRow[];
  void: InstallationAppointmentRow[];
}

export interface InstallationNoteRow {
  id: number;
  appointment_id: number;
  user_id: number | null;
  submitted_by: string | null;
  note: string;
  /** Set when the note was added from a checklist section (references that section). */
  section_id: string | null;
  section_title: string | null;
  created_at: string;
  updated_at: string;
}

async function jsonOrThrow<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || fallback);
  return data as T;
}

export async function fetchInstallationAppointments(): Promise<InstallationAppointmentsListResponse> {
  const res = await fetch(`${API_BASE}/api/installation-appointments`);
  return jsonOrThrow(res, 'Failed to fetch appointments');
}

export async function fetchInstallationAppointment(id: number): Promise<InstallationAppointmentRow> {
  const res = await fetch(`${API_BASE}/api/installation-appointments/${id}`);
  return jsonOrThrow(res, 'Failed to fetch appointment');
}

export interface SeedProduct {
  product_id?: string | null;
  product_name: string;
  tracks_serial?: boolean;
  quantity?: number;
}

export interface CreateInstallationAppointmentPayload {
  user_id: number;
  client_id?: number | null;
  order_number?: string | null;
  business_name: string;
  business_address?: string | null;
  point_of_contact?: string | null;
  poc_email?: string | null;
  poc_phone?: string | null;
  installation_at?: string | null;
  installation_end_at?: string | null;
  assignee_user_ids?: number[];
  checklist_state?: ChecklistState;
  products?: SeedProduct[];
}

export async function createInstallationAppointment(
  payload: CreateInstallationAppointmentPayload
): Promise<InstallationAppointmentRow> {
  const res = await fetch(`${API_BASE}/api/installation-appointments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res, 'Failed to create appointment');
}

export interface UpdateInstallationAppointmentPayload {
  business_name?: string;
  business_address?: string | null;
  point_of_contact?: string | null;
  poc_email?: string | null;
  poc_phone?: string | null;
  installation_at?: string | null;
  installation_end_at?: string | null;
  checklist_state?: ChecklistState;
  status?: InstallationAppointmentStatus;
  void_reason?: string | null;
  acting_user_id?: number;
  /** Clears the outstanding scheduling-conflict flag. */
  resolve_conflict?: boolean;
}

export async function updateInstallationAppointment(
  id: number,
  payload: UpdateInstallationAppointmentPayload
): Promise<InstallationAppointmentRow> {
  const res = await fetch(`${API_BASE}/api/installation-appointments/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res, 'Failed to update appointment');
}

export function completeInstallationAppointment(id: number): Promise<InstallationAppointmentRow> {
  return updateInstallationAppointment(id, { status: 'Completed' });
}

export function voidInstallationAppointment(
  id: number,
  reason: string,
  actingUserId?: number
): Promise<InstallationAppointmentRow> {
  return updateInstallationAppointment(id, { status: 'Void', void_reason: reason, acting_user_id: actingUserId });
}

/**
 * Reschedule to a new date/time; this also resolves any outstanding conflict.
 * Pass `installationEndAtIso` to make it span a date range, or null for a single day.
 */
export function rescheduleInstallationAppointment(
  id: number,
  installationAtIso: string,
  installationEndAtIso: string | null = null
): Promise<InstallationAppointmentRow> {
  return updateInstallationAppointment(id, {
    installation_at: installationAtIso,
    installation_end_at: installationEndAtIso,
    resolve_conflict: true,
  });
}

/** True when the appointment has an unresolved client-reported scheduling conflict. */
export function hasOpenConflict(r: {
  conflict_reported_at: string | null;
  conflict_resolved_at: string | null;
}): boolean {
  return !!r.conflict_reported_at && !r.conflict_resolved_at;
}

// ── Products ──

export async function fetchInstallationProducts(id: number): Promise<InstallationProductRow[]> {
  const res = await fetch(`${API_BASE}/api/installation-appointments/${id}/products`);
  const data = await jsonOrThrow<{ products: InstallationProductRow[] }>(res, 'Failed to load products');
  return data.products || [];
}

export interface AddInstallationProductPayload {
  product_id?: string | null;
  product_name: string;
  unit_id?: string | null;
  quantity?: number;
  tracks_serial?: boolean;
}

export async function addInstallationProduct(
  id: number,
  payload: AddInstallationProductPayload
): Promise<InstallationProductRow> {
  const res = await fetch(`${API_BASE}/api/installation-appointments/${id}/products`, {
    method: 'POST',
    headers: actorJsonHeaders(),
    body: JSON.stringify(withActor(payload)),
  });
  const data = await jsonOrThrow<{ product: InstallationProductRow }>(res, 'Failed to add product');
  return data.product;
}

export async function assignInstallationProductUnit(
  id: number,
  productId: number,
  unitId: string
): Promise<InstallationProductRow> {
  const res = await fetch(`${API_BASE}/api/installation-appointments/${id}/products/${productId}`, {
    method: 'PATCH',
    headers: actorJsonHeaders(),
    body: JSON.stringify(withActor({ unit_id: unitId })),
  });
  const data = await jsonOrThrow<{ product: InstallationProductRow }>(res, 'Failed to update product');
  return data.product;
}

export async function deleteInstallationProduct(id: number, productId: number): Promise<void> {
  const res = await fetch(`${API_BASE}/api/installation-appointments/${id}/products/${productId}`, {
    method: 'DELETE',
    headers: actorJsonHeaders(),
    body: JSON.stringify(withActor({})),
  });
  await jsonOrThrow(res, 'Failed to remove product');
}

// ── Notes ──

export async function fetchInstallationNotes(id: number): Promise<InstallationNoteRow[]> {
  const res = await fetch(`${API_BASE}/api/installation-appointments/${id}/notes`);
  const data = await jsonOrThrow<{ notes: InstallationNoteRow[] }>(res, 'Failed to load notes');
  return data.notes || [];
}

export async function createInstallationNote(
  id: number,
  userId: number,
  note: string,
  section?: { id: string; title: string } | null
): Promise<InstallationNoteRow> {
  const res = await fetch(`${API_BASE}/api/installation-appointments/${id}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_id: userId,
      note,
      ...(section ? { section_id: section.id, section_title: section.title } : {}),
    }),
  });
  return jsonOrThrow(res, 'Failed to add note');
}

// ── Customer notes (client schedule-conflict submissions) ──

export interface InstallationCustomerNoteRow {
  id: number;
  appointment_id: number;
  author_name: string | null;
  kind: string;
  note: string;
  created_at: string;
}

export async function fetchInstallationCustomerNotes(id: number): Promise<InstallationCustomerNoteRow[]> {
  const res = await fetch(`${API_BASE}/api/installation-appointments/${id}/customer-notes`);
  const data = await jsonOrThrow<{ notes: InstallationCustomerNoteRow[] }>(res, 'Failed to load customer notes');
  return data.notes || [];
}

// ── Public schedule-conflict form (no auth; keyed by opaque token) ──

export interface PublicInstallationConflictInfo {
  business_name: string;
  business_address: string | null;
  installation_at: string | null;
  status: InstallationAppointmentStatus;
}

export async function fetchPublicInstallationConflict(token: string): Promise<PublicInstallationConflictInfo> {
  const res = await fetch(`${API_BASE}/api/public/installation-conflict/${encodeURIComponent(token)}`);
  return jsonOrThrow(res, 'Failed to load appointment');
}

export async function submitInstallationConflict(
  token: string,
  reason: string,
  authorName?: string
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/public/installation-conflict/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason, author_name: authorName }),
  });
  await jsonOrThrow(res, 'Failed to submit');
}

// ── Checklist photo attachments ──

export interface InstallationChecklistAttachmentRow extends AttachmentVoidFields {
  id: number;
  appointment_id: number;
  section_id: string;
  original_file_name: string;
  uploaded_by_user_id: number | null;
  uploaded_by: string | null;
  created_at: string;
}

export async function fetchInstallationChecklistAttachments(
  id: number
): Promise<InstallationChecklistAttachmentRow[]> {
  const res = await fetch(`${API_BASE}/api/installation-appointments/${id}/checklist-attachments`);
  const data = await jsonOrThrow<{ attachments: InstallationChecklistAttachmentRow[] }>(res, 'Failed to load checklist photos');
  return data.attachments || [];
}

export async function uploadInstallationChecklistAttachment(
  id: number,
  sectionId: string,
  file: File,
  userId?: number | null
): Promise<InstallationChecklistAttachmentRow> {
  const form = new FormData();
  form.append('file', file);
  form.append('section_id', sectionId);
  if (userId != null && userId > 0) form.append('user_id', String(userId));
  const res = await fetch(`${API_BASE}/api/installation-appointments/${id}/checklist-attachments`, {
    method: 'POST',
    body: form,
  });
  return jsonOrThrow(res, 'Failed to upload photo');
}

export function installationChecklistAttachmentDownloadUrl(id: number, attachmentId: number): string {
  return `${API_BASE}/api/installation-appointments/${id}/checklist-attachments/${attachmentId}/download`;
}

/** Sets a photo aside with a reason. The photo stays on the appointment and can be restored. */
export function voidInstallationChecklistAttachment(
  id: number,
  attachmentId: number,
  reason: string,
  userId?: number | null
): Promise<InstallationChecklistAttachmentRow> {
  return patchAttachmentVoid(
    `${API_BASE}/api/installation-appointments/${id}/checklist-attachments/${attachmentId}`,
    reason,
    userId
  );
}

export function unvoidInstallationChecklistAttachment(
  id: number,
  attachmentId: number,
  userId?: number | null
): Promise<InstallationChecklistAttachmentRow> {
  return patchAttachmentVoid(
    `${API_BASE}/api/installation-appointments/${id}/checklist-attachments/${attachmentId}`,
    null,
    userId
  );
}
